import React from 'react';
import { Box, Divider, Drawer } from '@mui/material';
import { useParkingLocationForm } from '../../hooks/useParkingLocationForm';
import { FormProvider } from 'react-hook-form';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import HeaderSection from './sections/HeaderSection';
import ConfirmationDialog from './ConfirmationDialog';
import NameZoneSection from './sections/NameZoneSection';
import RulesSection from './sections/RulesSection';
import TowInstructionsSection from './sections/TowInstructionsSection';
import ActionButtonsSection from './sections/ActionButtonsSection';

interface ParkingLocationDrawerProps {
	onClose: () => void;
	selectedParkingLocation: ParkingLocation;
	onSave: (updatedList: ParkingLocation[]) => void;
}

const ParkingLocationDrawer: React.FC<ParkingLocationDrawerProps> = ({
	onClose,
	selectedParkingLocation,
	onSave,
}) => {
	const {
		formMethods,
		openConfirmDialog,
		loading,
		handleDelete,
		setOpenConfirmDialog,
		onSubmit,
	} = useParkingLocationForm(selectedParkingLocation, onSave, onClose);

	const {
		handleSubmit,
		formState: { isSubmitting },
	} = formMethods;

	const isNew = selectedParkingLocation?.id === '0';

	return (
		<Drawer anchor="right" open={true} onClose={onClose}>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Box
						sx={{
							width: { xs: '100vw', sm: 525, md: 600 },
							maxWidth: '100%',
							padding: 2,
							gap: 1,
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<HeaderSection
							isNewParkingLocation={isNew}
							onClose={onClose}
							loading={isSubmitting || loading}
						/>
						<NameZoneSection loading={isSubmitting || loading} />
						<Divider />
						<RulesSection loading={isSubmitting || loading} />
						<Divider />
						<TowInstructionsSection
							loading={isSubmitting || loading}
						/>
						<Divider />
						<ActionButtonsSection
							isNewParkingLocation={isNew}
							loading={isSubmitting || loading}
							onArchive={() => setOpenConfirmDialog(true)}
							onReset={() =>
								formMethods.reset(selectedParkingLocation)
							}
							onSave={handleSubmit(onSubmit)}
						/>
					</Box>
				</form>
			</FormProvider>

			<ConfirmationDialog
				open={openConfirmDialog}
				onClose={() => setOpenConfirmDialog(false)}
				onConfirm={async () => {
					await handleDelete();
					setOpenConfirmDialog(false);
				}}
				loading={isSubmitting || loading}
				title="Archive Parking Location"
				description="Are you sure you want to archive this parking location? This action cannot be undone."
				confirmText="Archive"
				cancelText="Cancel"
			/>
		</Drawer>
	);
};

export default ParkingLocationDrawer;
